import {
  createLibraryCategoryApi,
  createLibraryTagApi,
  deleteLibraryCategoryApi,
  deleteLibraryTagApi,
  listLibraryCategoriesApi,
  listLibraryTagsApi,
  updateLibraryCategoryApi,
  updateLibraryTagApi,
} from "../../../../service/StudyListService";

const emptyCatalog = () => ({ tags: [], categories: [] });

let catalog = emptyCatalog();
const listeners = new Set();

const copyCatalog = (source) => ({
  tags: (source?.tags || []).map((tag) => ({
    id: tag.id,
    name: tag.name,
    categoryIds: [...(tag.categoryIds || [])],
  })),
  categories: (source?.categories || []).map((category) => ({
    id: category.id,
    name: category.name,
    tagIds: [...(category.tagIds || [])],
  })),
});

const notify = () => {
  const snapshot = getLibraryCatalog();
  listeners.forEach((listener) => listener(snapshot));
};

const succeeded = (response) => response && response.data && response.data.result == "SUCCESS";

const messageOf = (response) => (response && response.data && response.data.message) || "";

const sameIds = (left, right) => {
  const a = [...(left || [])].map(Number).sort((x, y) => x - y);
  const b = [...(right || [])].map(Number).sort((x, y) => x - y);
  return a.length === b.length && a.every((id, index) => id === b[index]);
};

export const getLibraryCatalog = () => copyCatalog(catalog);

export const subscribeLibraryCatalog = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

let loading = null;

const fetchLibraryCatalog = async () => {
  const [tagResponse, categoryResponse] = await Promise.all([
    listLibraryTagsApi(),
    listLibraryCategoriesApi(),
  ]);
  if (!succeeded(tagResponse) || !succeeded(categoryResponse)) {
    return { error: messageOf(tagResponse) || messageOf(categoryResponse) };
  }
  catalog = {
    tags: (tagResponse.data.data || []).map((tag) => ({
      id: tag.id,
      name: tag.name,
      categoryIds: tag.categoryIds || [],
    })),
    categories: (categoryResponse.data.data || []).map((category) => ({
      id: category.id,
      name: category.name,
      tagIds: category.tagIds || [],
    })),
  };
  notify();
  return { catalog: getLibraryCatalog() };
};

const startLoad = () => {
  const promise = fetchLibraryCatalog().finally(() => {
    if (loading === promise) loading = null;
  });
  loading = promise;
  return promise;
};

export const loadLibraryCatalog = () => loading || startLoad();

export const saveLibraryCatalog = async (opened, current) => {
  const idMap = new Map();
  for (const tag of current.tags) {
    if (opened.tags.some((item) => item.id === tag.id)) continue;
    const response = await createLibraryTagApi(tag.name);
    if (!succeeded(response)) return { error: messageOf(response) };
    idMap.set(tag.id, response.data.data.id);
  }
  for (const tag of current.tags) {
    const previous = opened.tags.find((item) => item.id === tag.id);
    if (!previous || previous.name === tag.name) continue;
    const response = await updateLibraryTagApi(tag.id, tag.name);
    if (!succeeded(response)) return { error: messageOf(response) };
  }
  const realId = (id) => idMap.get(id) ?? id;
  for (const category of current.categories) {
    const tagIds = category.tagIds.map(realId);
    const previous = opened.categories.find((item) => item.id === category.id);
    if (!previous) {
      const response = await createLibraryCategoryApi(category.name, tagIds);
      if (!succeeded(response)) return { error: messageOf(response) };
      continue;
    }
    if (previous.name === category.name && sameIds(previous.tagIds, tagIds)) continue;
    const response = await updateLibraryCategoryApi(category.id, category.name, tagIds);
    if (!succeeded(response)) return { error: messageOf(response) };
  }
  for (const category of opened.categories) {
    if (current.categories.some((item) => item.id === category.id)) continue;
    const response = await deleteLibraryCategoryApi(category.id);
    if (!succeeded(response)) return { error: messageOf(response) };
  }
  for (const tag of opened.tags) {
    if (current.tags.some((item) => item.id === tag.id)) continue;
    const response = await deleteLibraryTagApi(tag.id);
    if (!succeeded(response)) return { error: messageOf(response) };
  }
  return startLoad();
};
